import type { LucideIcon } from "lucide-react";
import {
  User,
  BookOpen,
  Sparkles,
  Briefcase,
  GraduationCap,
  FolderGit2,
  Wrench,
  FileDown,
  Mail,
  Image as ImageIcon,
} from "lucide-react";
import type { SectionType } from "@profolio/validators";
import type { SectionData } from "@profolio/templates";

export interface SectionMeta {
  label: string;
  icon: LucideIcon;
  description: string;
  badgeLabel: string;
}

export const SECTION_METADATA: Record<SectionType, SectionMeta> = {
  about: {
    label: "About Me",
    icon: User,
    description: "Biography, profile narrative, and academic summary.",
    badgeLabel: "Bio",
  },
  publications: {
    label: "Publications",
    icon: BookOpen,
    description: "Research papers, journal articles, books, and preprints.",
    badgeLabel: "Papers",
  },
  research: {
    label: "Research Focus",
    icon: Sparkles,
    description: "Core research themes, pillars, and keyword tags.",
    badgeLabel: "Areas",
  },
  experience: {
    label: "Experience",
    icon: Briefcase,
    description: "Academic appointments, faculty roles, and leadership.",
    badgeLabel: "Roles",
  },
  teaching: {
    label: "Teaching & Courses",
    icon: GraduationCap,
    description: "Lectures, undergraduate/graduate courses, and syllabi.",
    badgeLabel: "Courses",
  },
  projects: {
    label: "Projects & Grants",
    icon: FolderGit2,
    description: "Funded research grants, PI roles, and sponsor awards.",
    badgeLabel: "Grants",
  },
  skills: {
    label: "Skills & Methods",
    icon: Wrench,
    description: "Technical skills, laboratory methods, and analytical tools.",
    badgeLabel: "Skills",
  },
  downloads: {
    label: "Downloads & Resources",
    icon: FileDown,
    description: "Course slides, lecture notes, datasets, and paper PDFs.",
    badgeLabel: "Files",
  },
  contact: {
    label: "Contact & Profiles",
    icon: Mail,
    description: "Email, office, and academic profiles (Scholar, ORCID, etc.).",
    badgeLabel: "Links",
  },
  media: {
    label: "Media & Visuals",
    icon: ImageIcon,
    description: "Photos, galleries, touch carousels, video embeds, and dividers.",
    badgeLabel: "Media",
  },
};

export const ALL_SECTION_TYPES: SectionType[] = [
  "about",
  "publications",
  "research",
  "experience",
  "teaching",
  "projects",
  "skills",
  "downloads",
  "contact",
  "media",
];

/**
 * Canonical default content factory for every section type.
 * Generates payloads that strictly satisfy @profolio/validators schemas.
 */
export function createDefaultSectionContent(type: SectionType): Record<string, unknown> {
  switch (type) {
    case "about":
      return {
        heading: "About Me",
        body: "",
        highlights: [],
      };
    case "publications":
      return {
        heading: "Publications",
        displayStyle: "grouped_by_year",
        items: [],
      };
    case "research":
      return {
        heading: "Research Interests",
        summary: "",
        areas: [],
      };
    case "experience":
      return {
        heading: "Experience",
        items: [],
      };
    case "teaching":
      return {
        heading: "Teaching & Courses",
        displayStyle: "grouped_by_level",
        items: [],
      };
    case "projects":
      return {
        heading: "Projects & Grants",
        displayStyle: "cards",
        items: [],
      };
    case "skills":
      return {
        heading: "Skills & Methodologies",
        displayStyle: "categorized_cards",
        groups: [],
      };
    case "downloads":
      return {
        heading: "Downloads & Resources",
        items: [],
      };
    case "contact":
      return {
        heading: "Contact & Profiles",
        email: "",
        phone: "",
        office: "",
        address: "",
        website: "",
        showContactForm: false,
        socialLinks: [],
      };
    case "media":
      return {
        heading: "",
        description: "",
        mediaType: "image",
        videoUrl: "",
        items: [],
        aspectRatio: "auto",
        galleryLayout: "grid",
        alignment: "center",
      };
  }
}

/**
 * Type-safe item count extractor for section badges.
 */
export function getSectionItemCount(section: SectionData): number | null {
  const c = (section.content as Record<string, unknown>) || {};
  switch (section.type) {
    case "about":
      return Array.isArray(c.highlights) && c.highlights.length > 0 ? c.highlights.length : null;
    case "publications":
      return Array.isArray(c.items) ? c.items.length : 0;
    case "research":
      return Array.isArray(c.areas) ? c.areas.length : 0;
    case "experience":
      return Array.isArray(c.items) ? c.items.length : 0;
    case "teaching":
      return Array.isArray(c.items) ? c.items.length : 0;
    case "projects":
      return Array.isArray(c.items) ? c.items.length : 0;
    case "skills": {
      if (!Array.isArray(c.groups)) return 0;
      return c.groups.reduce((acc: number, g: { skills?: unknown[] }) => {
        return acc + (Array.isArray(g.skills) ? g.skills.length : 0);
      }, 0);
    }
    case "downloads":
      return Array.isArray(c.items) ? c.items.length : 0;
    case "contact":
      return Array.isArray(c.socialLinks) ? c.socialLinks.length : 0;
    case "media":
      return Array.isArray(c.items) && c.items.length > 0 ? c.items.length : (c.videoUrl ? 1 : null);
    default:
      return null;
  }
}
